import { useEffect, useRef, useState } from "react";

/**
 * Smooth count-up animation. Triggers when the element scrolls into view.
 * props:
 *  - end:       target number
 *  - duration:  ms (default 1600)
 *  - decimals:  decimal places (default 0)
 *  - prefix / suffix
 *  - format:    optional fn(value) → string (overrides decimals)
 */
export default function CountUp({ end, duration = 1600, decimals = 0, prefix = "", suffix = "", format }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(end * eased);
            if (p < 1) requestAnimationFrame(tick);
            else setValue(end);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);

  const display = format ? format(value) : value.toFixed(decimals);
  return <span ref={ref}>{prefix}{display}{suffix}</span>;
}
